import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { DEALER } from "@/data/dealer";
import { findById, gbp, latestArrivals, vehiclePath, vehicles } from "@/lib/inventory";
import { EASE } from "@/lib/site";
import { MaskedLines } from "@/components/motion/Reveal";
import { SlashCorner } from "@/components/site/Shared";

// Real stock shown in the hero — swap IDs to feature different cars
const FEATURED_IDS = ["202609296512144", "202607144171530", "202610066738160", "202610157010943"];

const fadeUp = (delay) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, ease: EASE, delay } });

const Stat = ({ value, label }) => (
  <div className="min-w-0">
    <p className="font-display text-xl font-extrabold text-white sm:text-2xl">{value}</p>
    <p className="mt-1 text-xs leading-snug text-zinc-400 sm:text-sm">{label}</p>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const featured = (() => {
    const picked = FEATURED_IDS.map(findById).filter((v) => v?.images?.length);
    return picked.length >= 2 ? picked : latestArrivals(4);
  })();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "12%"]);
  const count = featured.length;

  useEffect(() => {
    if (reduce || paused || count < 2) return undefined;
    const t = setInterval(() => setActive((x) => (x + 1) % count), 6000);
    return () => clearInterval(t);
  }, [reduce, paused, count]);

  const cur = featured[active];

  return (
    <section ref={ref} className="on-dark relative bg-charcoal text-white" data-testid="hero-section">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-brand/60" />
      <div className="container-x relative grid gap-10 pb-28 pt-10 sm:pt-14 lg:grid-cols-12 lg:gap-8 lg:pb-36 lg:pt-20">
        <div className="relative z-10 flex min-w-0 flex-col justify-center lg:col-span-6 lg:pr-4">
          <motion.p className="eyebrow" {...fadeUp(0)}>
            Used cars · Peterborough
          </motion.p>
          <h1 className="mt-6 font-display text-[2.6rem] font-extrabold leading-[0.98] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            <MaskedLines lines={["Your next car", "starts here."]} delay={0.15} />
          </h1>
          <motion.p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-300 md:text-lg" {...fadeUp(0.45)}>
            Explore the latest used cars at YK Motors and find the right fit for your next journey.
          </motion.p>
          <motion.div className="mt-9 flex flex-col gap-3 sm:flex-row" {...fadeUp(0.6)}>
            <Link to="/used-cars" className="btn btn-primary" data-testid="hero-browse-cars-button">
              Browse Our Cars <ArrowRight className="arrow" aria-hidden="true" />
            </Link>
            <Link to="/contact" className="btn btn-outline-light" data-testid="hero-get-in-touch-button">
              Get in Touch
            </Link>
          </motion.div>
          <motion.div className="mt-12 grid max-w-lg grid-cols-3 gap-5 border-t border-white/15 pt-6" {...fadeUp(0.75)}>
            <Stat
              value={
                <span className="inline-flex items-center gap-1.5">
                  {DEALER.rating.score} <Star className="h-4 w-4 fill-current" aria-hidden="true" />
                </span>
              }
              label={`${DEALER.rating.reviews} AutoTrader reviews`}
            />
            <Stat value="7 days" label="Open, viewings by appointment" />
            <Stat value={vehicles.length} label="Cars currently listed" />
          </motion.div>
        </div>

        {cur && (
          <div className="relative min-w-0 lg:col-span-6" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <motion.div
              initial={{ opacity: 0, clipPath: "inset(0 0 0 100%)" }}
              animate={{ opacity: 1, clipPath: "inset(0 0 0 0%)" }}
              transition={{ duration: 1.2, ease: EASE, delay: 0.2 }}
            >
              <div className="cut-frame">
                <div className="cut-frame__clip relative aspect-[4/3] overflow-hidden bg-charcoal-2">
                  <motion.div style={{ y }} className="absolute inset-x-0 -bottom-[8%] -top-[8%]">
                    {featured.map((v, i) => (
                      <img
                        key={v.id}
                        src={v.images[0]}
                        alt={i === active ? `${v.year} ${v.make} ${v.model} in stock at YK Motors` : ""}
                        aria-hidden={i !== active}
                        loading={i === 0 ? "eager" : "lazy"}
                        fetchPriority={i === 0 ? "high" : "auto"}
                        className={cn(
                          "absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-[1400ms] ease-out",
                          i === active ? "scale-100 opacity-100" : "scale-[1.05] opacity-0",
                        )}
                      />
                    ))}
                  </motion.div>
                </div>
                <SlashCorner />
              </div>
            </motion.div>
            <Link
              to={vehiclePath(cur)}
              className="group relative mt-3 flex items-center gap-4 rounded-md bg-white p-4 text-ink transition-shadow hover:shadow-xl sm:absolute sm:bottom-6 sm:right-6 sm:mt-0 sm:w-[340px]"
              data-testid="hero-featured-vehicle-link"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand">In stock now</p>
                <p className="mt-1 truncate font-display font-extrabold">
                  {cur.year} {cur.make} {cur.model}
                </p>
                <p className="truncate text-sm text-steel">{cur.variant}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-display text-xl font-extrabold">{gbp(cur.price)}</p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-steel group-hover:text-brand">
                  View car <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </div>
            </Link>
            <div className="mt-4 flex items-center gap-2" role="group" aria-label="Featured cars">
              {featured.map((v, i) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Show ${v.year} ${v.make} ${v.model}`}
                  aria-pressed={i === active}
                  className="flex h-8 flex-1 items-center"
                  data-testid={`hero-slide-${i}-button`}
                >
                  <span className="relative h-[3px] w-full overflow-hidden bg-white/20">
                    <span
                      key={i === active ? `on-${active}-${paused}` : `off-${i}`}
                      className={cn(
                        "absolute inset-y-0 left-0 bg-brand-light",
                        i === active ? (paused || reduce ? "w-full" : "hero-progress") : i < active ? "w-full opacity-40" : "w-0",
                      )}
                    />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
